// The app's canvas on web.
//
// Every screen sizes itself from `Dimensions.get('window')` (directly, or via
// src/utils/responsive.ts, whose scale factor is viewport width ÷ 390). In a
// desktop browser that would treat a 1900px window as a giant phone: text,
// inputs and spacing balloon and layouts built for a portrait phone break.
//
// The web shell therefore renders the app inside a centred canvas no wider
// than CANVAS_MAX_WIDTH, and the `react-native` shim reports that same
// clamped width from `Dimensions` / `useWindowDimensions`, so what a screen
// measures is what it is drawn into. Height is clamped to the tallest phone
// the design system targets so vertical scaling stays in the range the
// mobile app already handles.
//
// Below CANVAS_MAX_WIDTH (real phones, narrow windows) nothing is clamped and
// the app renders exactly as it does natively.
//
// A window can also report 0 x 0 while the bundle evaluates: a tab that is
// created hidden, prerendered, or still being laid out by an embedding pane.
// `src/utils/responsive.ts` captures `Dimensions.get('window')` once at
// import and scales every size from it, so a zero width would make every
// scaled width, margin and font collapse for the life of the page (a 24px
// checkbox drawn as a 4px dot, "1." wrapping after the "1"). Zero is never a
// real viewport, so it is replaced by the phone the design system is based
// on; a window that later gets its real size resizes from there like any
// other window.

export const CANVAS_MAX_WIDTH = 480;
export const CANVAS_MAX_HEIGHT = 932;
export const FALLBACK_WIDTH = 390;
export const FALLBACK_HEIGHT = 844;

export interface Dims {
  width: number;
  height: number;
  scale: number;
  fontScale: number;
}

export function clampDims<T extends { width: number; height: number }>(d: T): T {
  const width = d.width > 0 ? Math.min(d.width, CANVAS_MAX_WIDTH) : FALLBACK_WIDTH;
  const height = d.height > 0 ? Math.min(d.height, CANVAS_MAX_HEIGHT) : FALLBACK_HEIGHT;
  if (width === d.width && height === d.height) return d;
  return { ...d, width, height };
}
